import { useEffect, useRef } from "react"

import debounce from "@utils/debounce"

/**
 * Hook that creates a ref, tracks the element's height, and sets it as a CSS variable on the document root
 * @param cssVariableName - Name of the CSS variable to set (e.g., "--popover-header-height")
 * @param condition - Optional condition to re-calculate height when they change
 * @returns A ref to attach to the element you want to track
 */
export default function useHeightCSSVariable<T extends HTMLElement = HTMLDivElement>(
    cssVariableName: string,
    condition: boolean = true,
) {
    const elementRef = useRef<T>(null)

    useEffect(() => {
        const element = elementRef.current

        if (!element || !condition) {
            return
        }

        const setHeight = () => {
            document.documentElement.style.setProperty(cssVariableName, `${element.clientHeight}px`)
        }

        // Set initial height immediately
        setHeight()

        // Debounce subsequent resize observations to avoid excessive updates
        const RESIZE_CALLBACK_DEBOUNCE_TIME = 100
        const resizeObserver = new ResizeObserver(debounce(setHeight, RESIZE_CALLBACK_DEBOUNCE_TIME))
        resizeObserver.observe(element)

        return () => {
            resizeObserver.disconnect()
            document.documentElement.style.removeProperty(cssVariableName)
        }
    }, [cssVariableName, condition])

    return elementRef
}
